import { apiClient, apiCall } from './api';

// ---------------------------------------------------------------------------
// Types matching backend response schemas
// ---------------------------------------------------------------------------

export interface AuthorityItem {
    id: number;
    name: string;
    ico: string;
}

export interface GuidelineItem {
    id: number;
    authority_id: number;
    file_name: string;
    file_size: number;
    content_type: string;
    is_active: boolean;
    uploaded_at: string;
    uploaded_by_user_id: number | null;
}

export interface UserMeResponse {
    id: number;
    email: string;
    display_name: string;
    authority_id: number | null;
}

export interface UserListItem {
    id: number;
    email: string;
    authority_id: number | null;
    authority_name: string | null;
    is_active: boolean;
    last_login_at: string | null;
    created_at: string;
}

// ---------------------------------------------------------------------------
// Authority endpoints
// ---------------------------------------------------------------------------

/**
 * List all active authorities (for dropdowns).
 */
export const getAuthorities = async (): Promise<AuthorityItem[]> => {
    const response = await apiCall(apiClient.get<AuthorityItem[]>('/authorities'));
    return response.data;
};

// ---------------------------------------------------------------------------
// User endpoints
// ---------------------------------------------------------------------------

/**
 * Get the authenticated user's profile. Identity is derived from the bearer
 * JWT (Entra ID) — no params are needed or honoured.
 */
export const getCurrentUser = async (): Promise<UserMeResponse> => {
    const response = await apiCall(apiClient.get<UserMeResponse>('/users/me'));
    return response.data;
};

/**
 * Read-only user directory. SystemAdmin / MmrOversight receive all users;
 * other roles receive only users within their own authority. Roles are not
 * included — they are managed in Entra ID.
 */
export const getUsers = async (): Promise<UserListItem[]> => {
    const response = await apiCall(apiClient.get<UserListItem[]>('/users'));
    return response.data;
};

// ---------------------------------------------------------------------------
// Guideline endpoints
// ---------------------------------------------------------------------------

/**
 * List all non-deleted guidelines for an authority.
 */
export const getGuidelines = async (authorityId: number): Promise<GuidelineItem[]> => {
    const response = await apiCall(
        apiClient.get<GuidelineItem[]>(`/authorities/${authorityId}/guidelines`)
    );
    return response.data;
};

/**
 * Upload a new guideline file (PDF, DOCX, or ZIP) for an authority.
 * The file is NOT set as active automatically.
 */
export const uploadGuideline = async (
    authorityId: number,
    file: File
): Promise<GuidelineItem> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiCall(
        apiClient.post<GuidelineItem>(
            `/authorities/${authorityId}/guidelines`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        )
    );
    return response.data;
};

/**
 * Set a guideline as active. All other guidelines for the authority are
 * automatically deactivated.
 */
export const activateGuideline = async (
    authorityId: number,
    guidelineId: number
): Promise<void> => {
    await apiCall(
        apiClient.put(`/authorities/${authorityId}/guidelines/${guidelineId}/activate`)
    );
};

/**
 * Soft-delete a guideline and remove its blob.
 */
export const deleteGuideline = async (
    authorityId: number,
    guidelineId: number
): Promise<void> => {
    await apiCall(
        apiClient.delete(`/authorities/${authorityId}/guidelines/${guidelineId}`)
    );
};

/**
 * Download a guideline file and trigger a browser download.
 * Returns the filename used for the download.
 */
export const downloadGuideline = async (
    authorityId: number,
    guidelineId: number,
    fileName: string
): Promise<void> => {
    const response = await apiClient.get(
        `/authorities/${authorityId}/guidelines/${guidelineId}/download`,
        { responseType: 'blob' }
    );

    const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Open a guideline in a new browser tab. Uses the authenticated download
 * endpoint (the backend needs the bearer token), wraps the response in a
 * blob URL, and lets the browser decide how to render it — PDFs preview
 * inline; DOCX/XLSX fall back to a download prompt (browser limitation).
 */
export const viewGuideline = async (
    authorityId: number,
    guidelineId: number
): Promise<void> => {
    const response = await apiClient.get(
        `/authorities/${authorityId}/guidelines/${guidelineId}/download`,
        { responseType: 'blob' }
    );

    const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
    });
    const url = window.URL.createObjectURL(blob);
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) {
        // Popup blocked — fall back to download so the user still gets the file.
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    // Revoke after the tab has had a chance to load (revoking immediately
    // would invalidate the URL before the new tab fetches it).
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};
